import { FC, useCallback, useRef, useState } from "react"
import ReactDatePicker from "react-datepicker"
import { endOfDay } from "date-fns"

import { useTranslation } from "react-i18next"

import { CustomCalendarContainer } from "./CustomCalendarContainer"

import "react-datepicker/dist/react-datepicker.css"
import "./datepicker.css"

interface IProps {
  timeframe: (Date | null)[]
  setTimeframe: (dates: (Date | null)[]) => void
  onTimeframeChange: (timeframe: Date[]) => void
  disabled?: boolean
  dateFormat?: string
  showTime?: boolean
  extendedPresetOptions?: boolean
}

type PendingSource = "manual" | "preset"

export const TimeRangePicker: FC<IProps> = (props: IProps) => {
  const { t } = useTranslation()
  const { timeframe, setTimeframe, onTimeframeChange, disabled = false, dateFormat = "dd.MM.yyyy", showTime = false } = props

  const [pendingTimeframe, setPendingTimeframe] = useState<(Date | null)[]>(timeframe)
  const [pendingSource, setPendingSource] = useState<PendingSource>("manual")

  const datepickerRef = useRef<ReactDatePicker | null>(null)
  const closePicker = useCallback(() => {
    datepickerRef.current?.setOpen(false)
  }, [])

  const handleDateRangeChange = useCallback(
    (dates: [Date | null, Date | null]) => {
      const [start, end] = dates
      setPendingSource("manual")

      if (showTime) {
        setPendingTimeframe([start, end])
        return
      }

      setTimeframe([start, end])
    },
    [setTimeframe, showTime]
  )

  const handleApply = useCallback(() => {
    const [start, end] = pendingTimeframe
    if (!start || !end) return

    const shouldMakeEndInclusive = pendingSource === "manual"
    const normalizedEnd = shouldMakeEndInclusive ? endOfDay(end) : end

    const next = [start, normalizedEnd] as Date[]
    setTimeframe(next)
    onTimeframeChange(next)
  }, [pendingTimeframe, pendingSource, showTime, setTimeframe, onTimeframeChange])

  if (showTime) {
    return (
      <div className="flex gap-2 items-center">
        <ReactDatePicker
          ref={datepickerRef}
          customInput={
            <input className="w-full sm:w-[300px] input input-bordered rounded-full border bg-[#F5F5F5] text-black border-gray-200 dark:bg-[#0C0C1A] dark:text-white dark:border-[rgba(255,255,255,0.1)]" />
          }
          disabled={disabled}
          placeholderText={t("table.fromTo")}
          dateFormat={dateFormat}
          selectsRange
          startDate={pendingTimeframe[0]}
          endDate={pendingTimeframe[1]}
          selected={pendingTimeframe[0]}
          onChange={(dates) => handleDateRangeChange(dates as [Date | null, Date | null])}
          showMonthDropdown
          showYearDropdown
          shouldCloseOnSelect={false}
          calendarContainer={(dpProps) => (
            <CustomCalendarContainer
              {...dpProps}
              pendingTimeframe={pendingTimeframe}
              setPendingTimeframe={setPendingTimeframe}
              applyPendingTimeframe={handleApply}
              clearPendingTimeframe={() => setPendingTimeframe([null, null])}
              extendedPresetOptions={props.extendedPresetOptions}
              closePicker={closePicker}
              markPresetSelection={() => setPendingSource("preset")}
            />
          )}
          wrapperClassName="w-full"
        />
      </div>
    )
  }

  return (
    <ReactDatePicker
      customInput={<input className="w-full sm:w-[280px] input input-bordered" />}
      disabled={disabled}
      placeholderText={t("table.selectRange")}
      dateFormat={dateFormat}
      selectsRange
      startDate={timeframe[0]}
      endDate={timeframe[1]}
      selected={timeframe[0]}
      onChange={(dates) => handleDateRangeChange(dates as [Date | null, Date | null])}
      showMonthDropdown
      showYearDropdown
      wrapperClassName="w-full"
    />
  )
}
